import AddIcon from "@mui/icons-material/Add";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";

import {
  OUTIL_COLORS,
  paperSx,
  tableHeaderSx,
} from "./OutilContainer.styles";

const ACTION_FIELDS = [
  "objective",
  "strategicAxis",
  "action",
  "responsible",
  "startDate",
  "endDate",
  "evaluation",
  "observations",
];

const EVALUATION_OPTIONS = [
  { label: "Non évalué", value: "not_evaluated" },
  { label: "Atteint", value: "achieved" },
  { label: "Partiellement atteint", value: "partial" },
  { label: "Non atteint", value: "not_achieved" },
];

const FREQUENCY_OPTIONS = ["Mensuelle", "Trimestrielle", "Semestrielle", "Annuelle"];

const compactFieldSx = {
  width: "100%",
  "& .MuiInputBase-root": {
    backgroundColor: "#fff",
    borderRadius: "5px",
    fontSize: 10,
    minHeight: 32,
  },
  "& .MuiInputBase-input": { px: 0.8, py: 0.65 },
};

const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr");

const createAction = (processusId) => ({
  action: "",
  endDate: "",
  evaluation: "not_evaluated",
  id: `objective-${Date.now()}`,
  objective: "",
  observations: "",
  processusId,
  responsible: "",
  startDate: "",
  strategicAxis: "",
  suiviOi3: false,
});

const createIndicator = () => ({
  actual: "",
  frequency: "",
  id: `indicator-${Date.now()}`,
  indicator: "",
  target: "",
});

const SectionHeader = ({ icon, onAdd, title, buttonLabel }) => (
  <Box
    sx={{
      alignItems: "center",
      backgroundColor: OUTIL_COLORS.panelHeader,
      borderBottom: `1px solid ${OUTIL_COLORS.border}`,
      display: "flex",
      justifyContent: "space-between",
      minHeight: 48,
      px: 1.5,
    }}
  >
    <Typography color={OUTIL_COLORS.navy} fontSize={12} fontWeight={700}>
      {icon}
      {title}
    </Typography>
    <Button
      onClick={onAdd}
      size="small"
      startIcon={<AddIcon />}
      sx={{
        backgroundColor: "#d5ad3f",
        color: "#10263d",
        fontSize: 10,
        fontWeight: 700,
        textTransform: "none",
        "&:hover": { backgroundColor: "#c79c2f" },
      }}
      variant="contained"
    >
      {buttonLabel}
    </Button>
  </Box>
);

const ActionsTable = ({ onChange, processus, processusOptions, value }) => {
  const [search, setSearch] = useState("");
  const rows = useMemo(
    () => (Array.isArray(value.objectiveActions) ? value.objectiveActions : []),
    [value.objectiveActions]
  );

  const filteredRows = useMemo(() => {
    const query = normalizeText(search).trim();
    if (!query) return rows;
    return rows.filter((row) =>
      ACTION_FIELDS.some((field) => normalizeText(row[field]).includes(query))
    );
  }, [rows, search]);

  const setRows = (nextRows) => onChange("objectiveActions", nextRows);

  const updateRow = (rowId, field, nextValue) => {
    setRows(rows.map((row) => (row.id === rowId ? { ...row, [field]: nextValue } : row)));
  };

  const addRow = () => setRows([...rows, createAction(processus.processus_id)]);
  const deleteRow = (rowId) => setRows(rows.filter((row) => row.id !== rowId));

  return (
    <Paper elevation={0} role="region" aria-label="Plan d'action des objectifs" sx={{ ...paperSx, overflow: "hidden" }}>
      <SectionHeader
        buttonLabel="Ajouter"
        icon={<TrackChangesOutlinedIcon sx={{ fontSize: 16, mr: 0.7, verticalAlign: "middle" }} />}
        onAdd={addRow}
        title="Plan d'action — Objectifs qualité (§6.2)"
      />
      <Box sx={{ alignItems: "center", backgroundColor: "#f5f8fb", border: `1px solid ${OUTIL_COLORS.border}`, borderRadius: "6px", display: "flex", gap: 0.7, mx: 1.5, my: 1, px: 1.2, py: 0.8 }}>
        <LightbulbOutlinedIcon sx={{ color: "#d5ad3f", fontSize: 16 }} />
        <Typography color={OUTIL_COLORS.textMuted} fontSize={10.5}>Cliquez sur Ajouter pour créer localement une ligne d'objectif.</Typography>
      </Box>
      <Box sx={{ alignItems: "center", backgroundColor: "#edf3f8", display: "flex", flexWrap: "wrap", gap: 1, px: 1.5, py: 1 }}>
        <Typography color={OUTIL_COLORS.textMuted} fontSize={9.5} fontWeight={700}>RECHERCHE :</Typography>
        <TextField
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Filtrer toutes les colonnes..."
          size="small"
          sx={{ flex: 1, minWidth: 220, "& .MuiInputBase-root": { backgroundColor: "#fff", fontSize: 10.5, height: 34 } }}
          value={search}
        />
        <Typography color={OUTIL_COLORS.textMuted} fontSize={10}>{filteredRows.length} entrée(s)</Typography>
        <Button disabled={!search} onClick={() => setSearch("")} size="small" startIcon={<RestartAltIcon />} sx={{ color: OUTIL_COLORS.navy, fontSize: 10, fontWeight: 700, textTransform: "none" }}>Réinit.</Button>
      </Box>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table aria-label="Objectifs qualité" size="small" sx={{ minWidth: 1580, tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              {[
                ["Processus", 160], ["Objectifs", 175], ["Axe stratégique", 165], ["Actions", 180],
                ["Responsable", 160], ["Début", 130], ["Fin", 130], ["Évaluation", 155],
                ["Observations", 180], ["Suivi OI3", 100], ["Actions", 80],
              ].map(([label, width], index) => <TableCell key={`${label}-${index}`} sx={{ ...tableHeaderSx, width }}>{label}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 ? (
              <TableRow><TableCell align="center" colSpan={11} sx={{ color: "#8091a3", fontSize: 11, py: 2.2 }}>Aucune entrée — cliquez sur Ajouter.</TableCell></TableRow>
            ) : filteredRows.map((row) => (
              <TableRow key={row.id} sx={{ "&:nth-of-type(even)": { backgroundColor: "#f7f9fc" } }}>
                <TableCell>
                  <TextField onChange={(event) => updateRow(row.id, "processusId", event.target.value)} select size="small" sx={compactFieldSx} value={row.processusId}>
                    {processusOptions.map((option) => <MenuItem key={option.processus_id} value={option.processus_id}>{option.instance}</MenuItem>)}
                  </TextField>
                </TableCell>
                <TableCell><TextField onChange={(event) => updateRow(row.id, "objective", event.target.value)} placeholder="Objectif..." size="small" sx={compactFieldSx} value={row.objective} /></TableCell>
                <TableCell><TextField onChange={(event) => updateRow(row.id, "strategicAxis", event.target.value)} placeholder="Axe..." size="small" sx={compactFieldSx} value={row.strategicAxis} /></TableCell>
                <TableCell><TextField onChange={(event) => updateRow(row.id, "action", event.target.value)} placeholder="Action..." size="small" sx={compactFieldSx} value={row.action} /></TableCell>
                <TableCell><TextField onChange={(event) => updateRow(row.id, "responsible", event.target.value)} placeholder="Responsable..." size="small" sx={compactFieldSx} value={row.responsible} /></TableCell>
                <TableCell><TextField inputProps={{ "aria-label": "Date de début" }} onChange={(event) => updateRow(row.id, "startDate", event.target.value)} size="small" sx={compactFieldSx} type="date" value={row.startDate} /></TableCell>
                <TableCell><TextField inputProps={{ "aria-label": "Date de fin" }} onChange={(event) => updateRow(row.id, "endDate", event.target.value)} size="small" sx={compactFieldSx} type="date" value={row.endDate} /></TableCell>
                <TableCell>
                  <TextField onChange={(event) => updateRow(row.id, "evaluation", event.target.value)} select size="small" sx={compactFieldSx} value={row.evaluation}>
                    {EVALUATION_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                  </TextField>
                </TableCell>
                <TableCell><TextField onChange={(event) => updateRow(row.id, "observations", event.target.value)} placeholder="Observations..." size="small" sx={compactFieldSx} value={row.observations} /></TableCell>
                <TableCell align="center"><Checkbox checked={Boolean(row.suiviOi3)} inputProps={{ "aria-label": "Suivi OI3" }} onChange={(event) => updateRow(row.id, "suiviOi3", event.target.checked)} size="small" /></TableCell>
                <TableCell align="center"><Tooltip title="Supprimer"><IconButton aria-label="Supprimer l'objectif" onClick={() => deleteRow(row.id)} size="small" sx={{ backgroundColor: "#b62424", borderRadius: "5px", color: "#fff", "&:hover": { backgroundColor: "#941b1b" } }}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

const IndicatorsTable = ({ onChange, value }) => {
  const rows = Array.isArray(value.objectiveIndicators)
    ? value.objectiveIndicators
    : [{ ...createIndicator(), id: "initial-indicator" }];
  const setRows = (nextRows) => onChange("objectiveIndicators", nextRows);
  const updateRow = (rowId, field, nextValue) => setRows(rows.map((row) => (row.id === rowId ? { ...row, [field]: nextValue } : row)));
  const addRow = () => setRows([...rows, createIndicator()]);
  const deleteRow = (rowId) => setRows(rows.filter((row) => row.id !== rowId));

  return (
    <Paper elevation={0} role="region" aria-label="Indicateurs du processus" sx={{ ...paperSx, overflow: "hidden" }}>
      <SectionHeader
        buttonLabel="Ajouter indicateur"
        icon={<BarChartOutlinedIcon sx={{ fontSize: 16, mr: 0.7, verticalAlign: "middle" }} />}
        onAdd={addRow}
        title="Indicateurs — Tableau de bord processus"
      />
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table aria-label="Indicateurs et valeurs" size="small" sx={{ minWidth: 900, tableLayout: "fixed" }}>
          <TableHead><TableRow>{[["Indicateur", 260], ["Objectif %", 250], ["Valeur réelle", 250], ["Fréquence", 250], ["Actions", 100]].map(([label, width]) => <TableCell key={label} sx={{ ...tableHeaderSx, width }}>{label}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell align="center" colSpan={5} sx={{ color: "#8091a3", fontSize: 11, py: 2 }}>Aucun indicateur.</TableCell></TableRow>
            ) : rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell><TextField onChange={(event) => updateRow(row.id, "indicator", event.target.value)} placeholder="Indicateur..." size="small" sx={compactFieldSx} value={row.indicator} /></TableCell>
                <TableCell><TextField inputProps={{ max: 100, min: 0 }} onChange={(event) => updateRow(row.id, "target", event.target.value)} size="small" sx={compactFieldSx} type="number" value={row.target} /></TableCell>
                <TableCell><TextField onChange={(event) => updateRow(row.id, "actual", event.target.value)} size="small" sx={compactFieldSx} type="number" value={row.actual} /></TableCell>
                <TableCell><TextField onChange={(event) => updateRow(row.id, "frequency", event.target.value)} select size="small" sx={compactFieldSx} value={row.frequency}><MenuItem value="">—</MenuItem>{FREQUENCY_OPTIONS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}</TextField></TableCell>
                <TableCell align="center"><Tooltip title="Supprimer"><IconButton aria-label="Supprimer l'indicateur" onClick={() => deleteRow(row.id)} size="small" sx={{ backgroundColor: "#b62424", borderRadius: "7px", color: "#fff", height: 34, width: 42, "&:hover": { backgroundColor: "#941b1b" } }}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

const PlanActionsObjectifs = ({ onChange, processus, processusOptions = [], value }) => (
  <Box sx={{ display: "grid", gap: 1.5 }}>
    <ActionsTable onChange={onChange} processus={processus} processusOptions={processusOptions} value={value} />
    <IndicatorsTable onChange={onChange} value={value} />
  </Box>
);

export default PlanActionsObjectifs;
